import { formatCurrency, formatDate } from '../utils';

// Recalculate repayment schedule with new interest rate
export const recalculateScheduleWithInterestRate = (
  loan: any,
  newInterestRate: number
) => {
  const updatedRepayments = loan.repayments.map((repayment: any) => {
    // Skip repayments that are already paid
    if (repayment.status === 'Paid') return repayment;
    
    // For floating rate loans, recalculate interest based on new rate
    const principal = repayment.principal;
    
    // Simple interest calculation - in a real app this would use more complex amortization
    const yearFraction = 0.25; // Assuming quarterly payments
    const newInterest = Math.round(principal * (newInterestRate / 100) * yearFraction);
    
    return {
      ...repayment,
      interest: newInterest,
      total: principal + newInterest,
      recalculated: true,
    };
  });

  return updatedRepayments;
};

// Apply prepayment to schedule
export const applyPrepayment = (
  loan: any,
  prepaymentAmount: number,
  prepaymentDate: string,
  prepaymentType: 'reduce_emi' | 'reduce_tenure'
) => {
  let remainingPrepayment = prepaymentAmount;
  const updatedRepayments = [...loan.repayments];
  
  // Find future repayments
  const futureRepayments = updatedRepayments.filter(
    (r: any) => r.status !== 'Paid' && new Date(r.date) >= new Date(prepaymentDate)
  );
  
  if (prepaymentType === 'reduce_emi') {
    // Reduce EMI amount, keep tenure the same
    const totalRemainingPayments = futureRepayments.length;
    
    if (totalRemainingPayments > 0) {
      const reductionPerPayment = Math.floor(prepaymentAmount / totalRemainingPayments);
      
      return updatedRepayments.map((repayment: any) => {
        if (repayment.status !== 'Paid' && new Date(repayment.date) >= new Date(prepaymentDate)) {
          const newPrincipal = Math.max(0, repayment.principal - reductionPerPayment);
          // Recalculate interest based on reduced principal
          const yearFraction = 0.25; // Assuming quarterly payments
          const newInterest = Math.round(newPrincipal * (loan.interestRate / 100) * yearFraction);
          
          return {
            ...repayment,
            principal: newPrincipal,
            interest: newInterest,
            total: newPrincipal + newInterest,
            recalculated: true,
          };
        }
        return repayment;
      });
    }
  } else if (prepaymentType === 'reduce_tenure') {
    // Keep EMI the same, reduce number of payments
    let updatedPayments = [...updatedRepayments];
    let remainingAmount = prepaymentAmount;
    
    // Apply prepayment to future repayments starting from the last one
    for (let i = updatedPayments.length - 1; i >= 0 && remainingAmount > 0; i--) {
      const repayment = updatedPayments[i];
      
      if (repayment.status !== 'Paid' && new Date(repayment.date) >= new Date(prepaymentDate)) {
        if (remainingAmount >= repayment.total) {
          // Mark this repayment as prepaid
          updatedPayments[i] = {
            ...repayment,
            status: 'Prepaid',
            prepaidDate: prepaymentDate,
            recalculated: true,
          };
          remainingAmount -= repayment.total;
        } else {
          // Partially prepay this repayment
          const newTotal = repayment.total - remainingAmount;
          const ratio = newTotal / repayment.total;
          updatedPayments[i] = {
            ...repayment,
            principal: Math.round(repayment.principal * ratio),
            interest: Math.round(repayment.interest * ratio),
            total: newTotal,
            recalculated: true,
          };
          remainingAmount = 0;
        }
      }
    }
    
    return updatedPayments;
  }
  
  return updatedRepayments;
};

// Apply moratorium to schedule
export const applyMoratorium = (
  loan: any,
  startDate: string,
  endDate: string,
  deferInterest: boolean
) => {
  // Find repayments within the moratorium period
  return loan.repayments.map((repayment: any) => {
    const repaymentDate = new Date(repayment.date);
    const moratoriumStart = new Date(startDate);
    const moratoriumEnd = new Date(endDate);
    
    if (repayment.status !== 'Paid' && 
        repaymentDate >= moratoriumStart && 
        repaymentDate <= moratoriumEnd) {
      
      // Move the repayment date to after the moratorium period
      const monthsToAdd = Math.ceil((moratoriumEnd.getTime() - repaymentDate.getTime()) / (30 * 24 * 60 * 60 * 1000));
      const newDate = new Date(repaymentDate);
      newDate.setMonth(newDate.getMonth() + monthsToAdd);
      
      let additionalInterest = 0;
      
      if (!deferInterest) {
        // Calculate additional interest during moratorium period
        const yearFraction = monthsToAdd / 12;
        additionalInterest = Math.round(repayment.principal * (loan.interestRate / 100) * yearFraction);
      }
      
      return {
        ...repayment,
        date: newDate.toISOString().split('T')[0],
        interest: repayment.interest + additionalInterest,
        total: repayment.principal + repayment.interest + additionalInterest,
        moratoriumApplied: true,
        recalculated: true,
      };
    }
    return repayment;
  });
};

// Track repayment schedule change event
export const createScheduleChangeEvent = (
  loan: any,
  changeType: 'interest_rate' | 'prepayment' | 'moratorium',
  changeDetails: any
) => {
  const now = new Date();
  
  const event = {
    id: Math.floor(Math.random() * 1000000),
    date: now.toISOString().split('T')[0],
    type: changeType,
    details: changeDetails,
    loanId: loan.id,
    status: 'Pending Approval'
  };
  
  return event;
};
